import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Button } from '@mui/material';

interface PageHeaderContextType {
  headerAction: ReactNode;
  setHeaderAction: (node: ReactNode) => void;
  recordLimitInfo: RecordLimitInfo | null;
  setRecordLimitInfo: (info: RecordLimitInfo | null) => void;
}

interface RecordLimitInfo {
  totalRecords: number;
  displayedCount?: number;
  limit?: number;
  showAll: boolean;
  isLoading?: boolean;
  onShowAll?: () => void;
}

const PageHeaderContext = createContext<PageHeaderContextType>({
  headerAction: null,
  setHeaderAction: () => { },
  recordLimitInfo: null,
  setRecordLimitInfo: () => { }
});

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [headerAction, setHeaderActionState] = useState<ReactNode>(null);
  const [recordLimitInfo, setRecordLimitInfoState] = useState<RecordLimitInfo | null>(null);

  const setRecordLimitInfo = useCallback(
    (info: RecordLimitInfo | null) => setRecordLimitInfoState(info),
    []
  );

  const setHeaderAction = useCallback((node: ReactNode) => setHeaderActionState(node), []);
  return (
    <PageHeaderContext.Provider value={{ headerAction, setHeaderAction, recordLimitInfo, setRecordLimitInfo }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export const usePageHeader = () => useContext(PageHeaderContext);

export function usePageHeaderCollapse(isCollapsed: boolean, setIsCollapsed: (val: boolean | ((prev: boolean) => boolean)) => void) {
  const { setHeaderAction } = usePageHeader();

  useEffect(() => {
    setHeaderAction(
      <Button
        size="small"
        color="primary"
        variant="text"
        disableRipple
        endIcon={isCollapsed ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
        onClick={() => setIsCollapsed((prev: boolean) => !prev)}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          outline: 'none',
          '&:hover': { background: 'transparent' },
          '&:focus': { outline: 'none', border: 'none' },
          '&:focus-visible': { outline: 'none', border: 'none', outlineOffset: 0 },
          '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
          '&::after': {
            boxShadow: `none`
          },
          '&:active::after': {
            boxShadow: `none`
          },
        }}
      >
        {isCollapsed ? 'Expand' : 'Collapse'}
      </Button>
    );
    return () => setHeaderAction(null);
  }, [isCollapsed, setHeaderAction, setIsCollapsed]);
}
